import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import NotificationTemplateForm from "./components/NotificationTemplateForm";
import { fetchGroupOptions } from "./services/groups";
import { fetchNotificationTemplate, updateNotificationTemplate } from "./services/notificationTemplates";
import { NotificationGroup, NotificationTemplate, NotificationTemplateFormValues } from "./types/types";

export default function EditNotificationTemplate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<NotificationTemplate | null>(null);
  const [groups, setGroups] = useState<NotificationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([fetchNotificationTemplate(id), fetchGroupOptions()])
      .then(([tmpl, groupOptions]) => {
        setTemplate(tmpl);
        setGroups(groupOptions);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !template || !id) {
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <Typography color="error">Notification template not found.</Typography>
      </Box>
    );
  }

  const handleSubmit = (values: NotificationTemplateFormValues) => {
    setSubmitting(true);
    updateNotificationTemplate(id, values)
      .then(() => {
        toast.success("Notification template updated", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
        navigate("/configurations/notification-manager");
      })
      .catch(() => {
        toast.error("Failed to update the notification template.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      })
      .finally(() => setSubmitting(false));
  };

  const { id: _templateId, updatedAt: _updatedAt, ...initialValues } = template;

  return (
    <NotificationTemplateForm
      initialValues={initialValues}
      groups={groups}
      submitLabel="Save Changes"
      submittingLabel="Saving..."
      onSubmit={handleSubmit}
      submitting={submitting}
    />
  );
}
